import { Injectable } from '@angular/core';
import {
  IListAnalyticsExchange,
  IListReport,
  ReportingApiService,
} from '@epicuro-next/data/reporting-api';
import { selectActiveAccountCurrency } from '@epicuro-next/state/accounts-state';
import { selectIsSameUserByExternalId } from '@epicuro-next/state/user-state';
import { isEqual, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { IBaseOutboundListReport } from '../../models/list-report';
import {
  loadListAnalytics,
  loadListBasketInsight,
  loadListLiveViewers,
  loadListsReports,
  reportDataReceived,
  updateAnalyticsExchange,
  upsertAnalyticsEntities,
} from '../analytics-entities.actions';
import { AnalyticsEntity } from '../interfaces';
import {
  selectCanSeeAnalytics,
  selectHotListDateRange,
  selectListAnalyticsEntity,
  selectListExchange,
} from '../selectors';
import { generateDefaultReport } from '../utils/generate-default-report';

@Injectable()
export class AnalyticsEntitiesEffects {
  private activeAccountCurrency$ = this.store.pipe(
    select(selectActiveAccountCurrency),
    distinctUntilChanged(),
  );

  private canSeeAnalytics$ = this.store.pipe(
    select(selectCanSeeAnalytics),
    distinctUntilChanged(),
  );

  private hotListDateRange$ = this.store.pipe(
    select(selectHotListDateRange),
    distinctUntilChanged<ReturnType<typeof selectHotListDateRange>>(isEqual),
  );

  public loadListAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListAnalytics),
      switchMap((action) => [
        loadListBasketInsight(action),
        loadListLiveViewers(action),
      ]),
    ),
  );

  public loadListAnalyticsReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListsReports),
      concatLatestFrom(() => [
        this.canSeeAnalytics$,
        this.activeAccountCurrency$,
        this.hotListDateRange$,
      ]),
      filter(([, canSeeAnalytics]) => !!canSeeAnalytics),
      mergeMap(([{ listIds }, , currency, dateRange]) => {
        return this.reportingService
          .getProductListReports(listIds, currency, dateRange)
          .pipe(
            switchMap((result) => {
              const entitiesToUpdate: AnalyticsEntity[] =
                productListReportsToPartialEntities(
                  listIds,
                  result.listData,
                  currency,
                );
              const exchangeAction =
                result.listData.length > 0
                  ? updateAnalyticsExchange({ exchange: result.exchangeRate })
                  : undefined;

              return [
                upsertAnalyticsEntities({ entities: entitiesToUpdate }),
                exchangeAction,
              ].filter(isNonNullish);
            }),
          );
      }),
    ),
  );

  public onListReportEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportDataReceived),
      map(({ event }) => event.payload),
      filter(isNonNullish),
      concatMap((report: IBaseOutboundListReport) =>
        of(report).pipe(
          withLatestFrom(
            this.store.pipe(
              select(selectIsSameUserByExternalId, report.externalOwnerId),
            ),
            this.store.pipe(
              select(selectListAnalyticsEntity, report.productListId),
            ),
            this.store.pipe(select(selectListExchange)),
          ),
          filter(([, isOwner, analyticsEntity]) =>
            Boolean(isOwner && analyticsEntity),
          ),
          map(
            ([item, , analyticsEntity, exchange]: [
              IBaseOutboundListReport,
              boolean,
              AnalyticsEntity,
              IListAnalyticsExchange,
            ]) => ({
              ...analyticsEntity,
              report: {
                ...analyticsEntity.report,
                ...(item as IListReport),
                ...((item as IListReport)?.placed && {
                  placed: {
                    ...(item as IListReport).placed,
                    revenueInRequestedCurrency: {
                      currency: exchange.currency,
                      value:
                        (item as IListReport).placed.revenue.value *
                        exchange.rate,
                    },
                  },
                }),
              },
            }),
          ),
          take(1),
        ),
      ),
      map((entity) => upsertAnalyticsEntities({ entities: [entity] })),
    ),
  );

  constructor(
    private actions$: Actions,
    private reportingService: ReportingApiService,
    private store: Store,
  ) {}
}

function productListReportsToPartialEntities(
  requestedListIds: string[],
  listReports: IListReport[],
  currency: string | null,
) {
  const listDataMap = analyticsResultToListsDataMap(listReports);
  return requestedListIds.map((listId): AnalyticsEntity => {
    const report = listDataMap[listId];
    return {
      listId,
      report: report ?? generateDefaultReport(listId, currency),
    };
  });
}

function analyticsResultToListsDataMap(result: IListReport[]) {
  return result.reduce(
    (prev, curr) => ({ ...prev, [curr.productListId]: curr }),
    {} as { [key: string]: IListReport },
  );
}
